import { PERMISSION_CATALOG, type Permission } from '../permissions/permission-catalog';
import { type DataScope } from './session.store';

/*
 * MOCK personas for the in-memory sign-in. A persona is data: a name, a permission set and a
 * data scope. No page code reads a role name, so a custom role needs no code change.
 */
export interface Persona {
  readonly displayName: string;
  readonly roles: readonly string[];
  readonly permissions: readonly Permission[];
  readonly flags: readonly string[];
  /** Whose records the list and report pages show. */
  readonly dataScope: DataScope;
}

/** Held by everyone signed in: their own clock, attendance, leave, payslip and statements. */
const SELF: readonly Permission[] = [
  'Hrm.Ess.ReadOwn',
  'Hrm.Clock.Use',
  'Hrm.AttendanceSummary.ViewOwn',
  'Hrm.Regularization.Create',
  'Hrm.LeaveRequest.Create',
  'Hrm.Payslip.ReadOwn',
  'Hrm.Statement.ReadOwn',
  'Hrm.Holiday.ViewOwn',
  'Hrm.WorkCalendar.ViewOwn',
  'Hrm.Roster.ViewOwn',
  'Hrm.ShiftChange.ViewOwn',
  'Hrm.Overtime.Request',
  'Hrm.CompOff.Request',
  'Hrm.Document.ReadOwn',
  'Hrm.Profile.ReadOwn',
  'Hrm.Profile.RequestChangeOwn',
  'Hrm.Dispute.ReadOwn',
  'Hrm.Dispute.CreateOwn',
  'Hrm.Helpdesk.ReadOwn',
  'Hrm.Helpdesk.CreateOwn',
  'Hrm.Policies.ReadOwn',
  'Hrm.Assets.ReadOwn',
  'Hrm.Announcements.ReadOwn',
];

const CEO: Persona = {
  displayName: 'CEO',
  roles: ['CEO'],
  dataScope: 'all',
  flags: [],
  // Read-heavy, plus the payroll approval. It has no Hrm.Payroll.Prepare: whoever prepares a run
  // cannot approve it.
  permissions: [
    ...SELF,
    'Hrm.Employees.View',
    'Hrm.Employee.Read',
    'Hrm.Company.Read',
    'Hrm.Audit.View',
    'Hrm.Approval.Read',
    'Hrm.Manager.Read',
    'Hrm.Manager.Approve',
    'Hrm.Location.Read',
    'Hrm.Department.Read',
    'Hrm.AttendanceView.Read',
    'Hrm.AttendanceSummary.View',
    'Hrm.AttendanceMonth.Read',
    'Hrm.LeaveRequest.Read',
    'Hrm.LeaveCalendar.Read',
    'Hrm.LeaveBalance.ReadAll',
    'Hrm.PayStructure.Read',
    'Hrm.Compensation.Read',
    'Hrm.PayrollRun.Read',
    'Hrm.Payroll.Approve',
    'Hrm.PayrollReport.Read',
    'Hrm.Incentive.Read',
    'Hrm.Requisition.Read',
    'Hrm.Candidate.Read',
    'Hrm.Probation.Read',
    'Hrm.Movement.Read',
    'Hrm.Cycle.Read',
    'Hrm.Appraisal.Review',
    'Hrm.Separation.Read',
    'Hrm.Announcements.Read',
    'Hrm.Reports.Read',
    'Hrm.Reports.PayrollCost',
    'Hrm.Settings.View',
    'Hrm.Plan.Read',
    'Hrm.Reports.Export',
    'Hrm.ReportSchedules.Read',
    'Hrm.Statement.ReadAll',
    'Hrm.Increment.Read',
    'Hrm.Bonus.Read',
    'Hrm.Commission.Read',
    'Hrm.Commission.Approve',
    'Hrm.Deduction.Read',
    'Hrm.Deduction.Approve',
    'Hrm.OvertimeRule.Read',
    'Hrm.Loan.Read',
    'Hrm.Loan.ApprovePayoff',
    'Hrm.Settlement.Read',
    'Hrm.Settlement.Approve',
    'Hrm.PayrollRun.ApproveReopen',
    'Hrm.PayrollRow.Read',
    'Hrm.PayrollSnapshot.Read',
    'Hrm.ParallelRun.Read',
    'Hrm.ParallelRun.SignOffHr',
    'Hrm.Incentive.Approve',
    'Hrm.CommissionRun.Read',
    'Hrm.CommissionRun.ReadDetail',
    'Hrm.CommissionRun.Approve',
    'Hrm.LeaveYearEnd.Read',
    'Hrm.LeaveYearEnd.Approve',
    'Hrm.Offer.Read',
    'Hrm.Offer.ReadSalary',
    'Hrm.Requisition.ReadBudget',
    'Hrm.Letter.Read',
    'Hrm.Letter.ReadSalary',
    'Hrm.Movement.ProposePay',
    'Hrm.Cycle.ProposePay',
    'Hrm.Training.ReadCost',
    'Hrm.Training.ReadCompliance',
    'Hrm.Course.Read',
    'Hrm.Certification.Read',
    'Hrm.JobPosting.Read',
    'Hrm.Policies.Read',
    'Hrm.ReportingLine.Read',
    'Hrm.EmployeeTimeline.Read',
    'Hrm.Holiday.View',
    'Hrm.Roster.View',
    'Hrm.Overtime.Read',
    'Hrm.CompOff.Read',
    'Hrm.Dispute.ReadAll',
  ],
};

const ADMIN_EXCLUDED =
  /^Hrm.(Operator|PlatformTenants|PlatformJobs).|.(Approve|ApprovePayoff|ApproveReopen)$|PlatformSecurity.RotateKeys$/;

const ADMIN_CODES: readonly Permission[] = PERMISSION_CATALOG.map((p) => p.code).filter(
  (code) => !ADMIN_EXCLUDED.test(code),
);

export const HR_ADMIN: Persona = {
  displayName: 'HR Admin',
  roles: ['HR Admin'],
  dataScope: 'all',
  flags: [],
  // Everything the catalog lists except the approve codes (the CEO approves) and the operator codes.
  permissions: [...new Set([...SELF, ...ADMIN_CODES])],
};

const HR_EXECUTIVE: Persona = {
  displayName: 'HR Executive',
  roles: ['HR Executive'],
  dataScope: 'all',
  flags: [],
  // Operational screens only: no settings, no pay, no restricted cases, no sensitive reveal.
  permissions: [
    ...SELF,
    'Hrm.Employees.View',
    'Hrm.Import.View',
    'Hrm.Approval.Read',
    'Hrm.Location.Read',
    'Hrm.Department.Read',
    'Hrm.Employee.Read',
    'Hrm.Employee.Create',
    'Hrm.Employee.Update',
    'Hrm.EmployeeDocument.Read',
    'Hrm.Invitation.View',
    'Hrm.Shift.View',
    'Hrm.AttendanceSummary.View',
    'Hrm.Regularization.View',
    'Hrm.AttendanceView.Read',
    'Hrm.AttendanceMonth.Read',
    'Hrm.LeaveBalance.ReadAll',
    'Hrm.LeaveRequest.Read',
    'Hrm.LeaveCalendar.Read',
    'Hrm.Requisition.Read',
    'Hrm.Candidate.Read',
    'Hrm.Checklist.Read',
    'Hrm.Probation.Read',
    'Hrm.Movement.Read',
    'Hrm.Letter.Read',
    'Hrm.Announcements.Read',
    'Hrm.Announcements.Create',
    'Hrm.Helpdesk.Read',
    'Hrm.Assets.Read',
    'Hrm.Reports.Read',
    'Hrm.Employee.Export',
    'Hrm.Invitation.Create',
    'Hrm.ReportingLine.Read',
    'Hrm.EmployeeTimeline.Read',
    'Hrm.Holiday.View',
    'Hrm.WorkCalendar.View',
    'Hrm.Roster.View',
    'Hrm.ShiftChange.View',
    'Hrm.Punch.View',
    'Hrm.Device.View',
    'Hrm.ManualEntry.View',
    'Hrm.ManualEntry.Create',
    'Hrm.Overtime.Read',
    'Hrm.CompOff.Read',
    'Hrm.CompOff.Manage',
    'Hrm.LeaveBalance.Read',
    'Hrm.LeavePolicy.Read',
    'Hrm.Policies.Read',
    'Hrm.Helpdesk.Respond',
    'Hrm.Helpdesk.Assign',
    'Hrm.Offer.Read',
    'Hrm.JobPosting.Read',
    'Hrm.CareerSite.Read',
    'Hrm.Course.Read',
    'Hrm.Certification.Read',
    'Hrm.Training.ReadCompliance',
    'Hrm.EmployeeDocument.Manage',
  ],
};

const TEAM_LEAD: Persona = {
  displayName: 'Team Lead',
  roles: ['Team Lead'],
  dataScope: 'reports',
  flags: [],
  // Direct reports only. No pay, payment account, national ID, disciplinary or appraisal codes.
  // Hrm.Payslip.ReadOwn (in SELF) is the person's own payslip.
  permissions: [
    ...SELF,
    'Hrm.Manager.Read',
    'Hrm.Manager.Approve',
    'Hrm.Approval.Read',
    'Hrm.Employee.Read',
    'Hrm.AttendanceView.Read',
    'Hrm.AttendanceSummary.View',
    'Hrm.Regularization.View',
    'Hrm.LeaveRequest.Read',
    'Hrm.LeaveCalendar.Read',
    'Hrm.Probation.Read',
    'Hrm.Requisition.Read',
    'Hrm.Reports.Read',
    'Hrm.Manager.ApproveBulk',
    'Hrm.Manager.Delegate',
    'Hrm.Manager.ReadAttendance',
    'Hrm.Manager.ReadLeave',
    'Hrm.Overtime.Read',
    'Hrm.CompOff.Read',
    'Hrm.Roster.View',
    'Hrm.ShiftChange.View',
    'Hrm.Holiday.View',
    'Hrm.Policies.Read',
  ],
};

const EMPLOYEE: Persona = {
  displayName: 'Employee',
  roles: ['Employee'],
  dataScope: 'own',
  flags: [],
  // Own data only: every code reads or uses the person's own record, or creates their own request.
  permissions: [...SELF, 'Hrm.Loan.Request'],
};

const OPERATOR: Persona = {
  displayName: 'Platform Operator',
  roles: ['Platform operator'],
  dataScope: 'all',
  flags: [],
  permissions: [
    'Hrm.PlatformTenants.Create',
    'Hrm.PlatformTenants.View',
    'Hrm.PlatformJobs.View',
    'Hrm.Operator.TenantRead',
    'Hrm.Operator.AuditRead',
  ],
};

/** The five company roles, in the order the "View as" list shows them. Keys are sign-in logins. */
export const COMPANY_PERSONA_KEYS = [
  'ceo',
  'admin',
  'hr-executive',
  'team-lead',
  'employee',
] as const;

/**
 * Logins the mock names. `admin` is the HR Admin and keeps its old key. `operator` is platform
 * staff and not one of the five company roles.
 */
export const MOCK_PERSONAS: Readonly<Record<string, Persona>> = {
  ceo: CEO,
  admin: HR_ADMIN,
  'hr-executive': HR_EXECUTIVE,
  'team-lead': TEAM_LEAD,
  employee: EMPLOYEE,
  operator: OPERATOR,
};
